import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, CAR_BRANDS, SERVICES_NEEDED, isValidPhone, errText } from "@/lib/api";
import { trackCallbackRequested } from "@/lib/pixel";

const TIMES = ["Morning", "Afternoon", "Evening"];

export default function CallbackForm() {
  const [form, setForm] = useState({
    name: "", phone: "", car: "", service_needed: "", message: "", preferred_time: "", website: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Please enter your name");
    if (!isValidPhone(form.phone)) return toast.error("Enter a valid 10-digit mobile number");
    if (!form.car) return toast.error("Select your car brand");
    if (!form.service_needed) return toast.error("Select the service you need");
    setBusy(true);
    try {
      await api.post("/callback", form);
      trackCallbackRequested({ service_needed: form.service_needed, car_brand: form.car, phone: form.phone, name: form.name });
      setDone(true);
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="callback" className="py-16 sm:py-28 px-5 lg:px-8 bg-[#F5F5F7] border-y border-[#E4E4E7]" data-testid="callback-section">
      <div className="max-w-3xl mx-auto">
        <p className="text-xs uppercase tracking-[0.25em] text-[#FF5A00] font-bold mb-3">No Games, Just Service</p>
        <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#0A0A0A]">
          Prefer to Talk? <span className="text-[#FF5A00]">We'll Call You.</span>
        </h2>
        <p className="mt-3 text-[#52525B]">Leave your details and our service advisor will call at your preferred time.</p>

        {done ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mt-10 bg-white border border-[#22C55E]/50 rounded-sm p-8 text-center"
            data-testid="callback-success"
          >
            <p className="text-2xl font-bold text-[#0A0A0A]">Thanks {form.name.split(" ")[0]}! 🙌</p>
            <p className="mt-2 text-[#52525B]">Our service advisor will call you at your preferred time.</p>
          </motion.div>
        ) : (
          <form onSubmit={submit} className="mt-10 bg-white border border-[#E4E4E7] rounded-sm p-6 sm:p-8 space-y-5">
            {/* Honeypot — hidden from humans */}
            <input
              type="text" name="website" value={form.website} tabIndex={-1} autoComplete="off"
              onChange={(e) => setForm({ ...form, website: e.target.value })}
              style={{ position: "absolute", left: "-9999px", height: 0, width: 0, opacity: 0 }}
              aria-hidden="true"
            />
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold text-[#52525B]">Name</Label>
                <Input data-testid="callback-name-input" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Your name" className="mt-2 bg-[#F5F5F7] border-[#E4E4E7] text-[#0A0A0A] focus:border-[#FF5A00]" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold text-[#52525B]">Mobile Number</Label>
                <Input data-testid="callback-phone-input" value={form.phone} maxLength={10} inputMode="numeric"
                  onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, "") })}
                  placeholder="10-digit mobile number" className="mt-2 bg-[#F5F5F7] border-[#E4E4E7] text-[#0A0A0A] focus:border-[#FF5A00]" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold text-[#52525B]">Car Brand &amp; Model</Label>
                <Select value={form.car} onValueChange={(v) => setForm({ ...form, car: v })}>
                  <SelectTrigger data-testid="callback-car-select" className="mt-2 bg-[#F5F5F7] border-[#E4E4E7] text-[#0A0A0A]">
                    <SelectValue placeholder="Select brand" />
                  </SelectTrigger>
                  <SelectContent>
                    {CAR_BRANDS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold text-[#52525B]">Service Needed</Label>
                <Select value={form.service_needed} onValueChange={(v) => setForm({ ...form, service_needed: v })}>
                  <SelectTrigger data-testid="callback-service-select" className="mt-2 bg-[#F5F5F7] border-[#E4E4E7] text-[#0A0A0A]">
                    <SelectValue placeholder="Select service" />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICES_NEEDED.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold text-[#52525B]">Message (optional)</Label>
              <Textarea data-testid="callback-message-input" value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Anything we should know about your car?"
                className="mt-2 bg-[#F5F5F7] border-[#E4E4E7] text-[#0A0A0A] focus:border-[#FF5A00] min-h-[90px]" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold text-[#52525B]">Preferred time to call</Label>
              <div className="mt-2 flex gap-3">
                {TIMES.map((t) => (
                  <button
                    key={t} type="button"
                    data-testid={`callback-time-${t.toLowerCase()}`}
                    onClick={() => setForm({ ...form, preferred_time: t })}
                    className={`px-5 py-2.5 rounded-sm border text-sm font-semibold transition-colors ${
                      form.preferred_time === t
                        ? "bg-[#FF5A00] border-[#FF5A00] text-white"
                        : "bg-white border-[#E4E4E7] text-[#52525B] hover:border-[#FF5A00]"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <Button data-testid="callback-submit-button" type="submit" disabled={busy}
              className="w-full bg-[#FF5A00] hover:bg-[#E04F00] font-bold py-6 text-base">
              {busy ? "Submitting..." : "Request My Callback"}
            </Button>
          </form>
        )}
      </div>
    </section>
  );
}
